import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { supabase } from '../config/supabase';

const StatBox = ({ title, value, bgcolor = 'primary.main', onClick }) => (
  <Paper
    sx={{
      p: 3,
      height: '100%',
      bgcolor,
      color: 'white',
      borderRadius: 3,
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      },
    }}
    onClick={onClick}
  >
    <Typography variant="h6" gutterBottom sx={{ opacity: 0.8 }}>
      {title}
    </Typography>
    <Typography variant="h3" component="div">
      {value}
    </Typography>
  </Paper>
);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
const AGE_GROUP_COLORS = {
  '0-12': '#60a5fa',   // Light blue
  '13-18': '#34d399',  // Emerald
  '19-25': '#fbbf24',  // Amber
  '26-35': '#f87171',  // Red
  '36-50': '#a78bfa',  // Purple
  '51+': '#f472b6',    // Pink
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalFamilies: 0,
    activeMembers: 0,
    inactiveMembers: 0,
    genderData: [],
    ageGroupData: []
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch total families
      const { count: familiesCount } = await supabase
        .from('families')
        .select('*', { count: 'exact' });

      // Fetch members by status
      const { data: membersData } = await supabase
        .from('contacts')
        .select('membership_status');

      const activeMembers = membersData?.filter(m => m.membership_status === 'Active').length || 0;
      const inactiveMembers = membersData?.filter(m => m.membership_status === 'Inactive').length || 0;

      // Fetch gender distribution
      const { data: genderData } = await supabase
        .from('contacts')
        .select('gender');

      const genderCounts = genderData?.reduce((acc, { gender }) => {
        if (gender) {
          acc[gender] = (acc[gender] || 0) + 1;
        }
        return acc;
      }, {});

      const genderChartData = Object.entries(genderCounts || {}).map(([name, value]) => ({
        name,
        value
      }));

      // Fetch age group distribution
      const { data: ageData } = await supabase
        .from('contacts')
        .select('age_group');

      const ageGroupCounts = ageData?.reduce((acc, { age_group }) => {
        if (age_group) {
          acc[age_group] = (acc[age_group] || 0) + 1;
        }
        return acc;
      }, {});

      const ageGroupChartData = Object.entries(ageGroupCounts || {}).map(([name, Members]) => ({
        name,
        Members
      }));

      setStats({
        totalFamilies: familiesCount || 0,
        activeMembers,
        inactiveMembers,
        genderData: genderChartData,
        ageGroupData: ageGroupChartData
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Dashboard
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatBox 
              title="Total Families" 
              value={stats.totalFamilies}
              bgcolor="#2563eb"
              onClick={() => navigate('/families')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatBox 
              title="Active Members" 
              value={stats.activeMembers}
              bgcolor="#16a34a"
              onClick={() => navigate('/contacts', { state: { filter: 'active' } })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatBox 
              title="Inactive Members" 
              value={stats.inactiveMembers}
              bgcolor="#dc2626"
              onClick={() => navigate('/contacts', { state: { filter: 'inactive' } })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatBox 
              title="Total Members" 
              value={stats.activeMembers + stats.inactiveMembers}
              bgcolor="#7c3aed"
              onClick={() => navigate('/contacts')}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3, height: '400px' }}>
              <Typography variant="h6" gutterBottom>
                Gender Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.genderData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3, height: '400px' }}>
              <Typography variant="h6" gutterBottom>
                Age Group Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.ageGroupData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Members">
                    {stats.ageGroupData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={AGE_GROUP_COLORS[entry.name]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}
